import apiClient from '@/lib/api/apiClient'
import { extractErrorMessage } from '@/lib/utils/errorHandling'
import type {
  ReleaseNote,
  ReleaseNoteListParams,
  ReleaseNoteListResponse,
  ReleaseNoteSubmitPayload,
} from '../types'

const BASE_ENDPOINT = '/api/release_notes'

export async function getReleaseNotes(
  params: ReleaseNoteListParams = {},
): Promise<ReleaseNoteListResponse> {
  try {
    const response = await apiClient.get<ReleaseNoteListResponse>(BASE_ENDPOINT, { params })
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to load release notes')
  }
}

export async function saveReleaseNote(
  payload: ReleaseNoteSubmitPayload,
  id?: number,
): Promise<ReleaseNote> {
  try {
    const url = id ? `${BASE_ENDPOINT}/${id}` : BASE_ENDPOINT
    const response = await apiClient.post<ReleaseNote>(url, payload)
    return response.data
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to save release note')
  }
}

export async function deleteReleaseNote(id: number): Promise<void> {
  try {
    await apiClient.delete(`${BASE_ENDPOINT}/${id}`)
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Failed to delete release note')
  }
}

/**
 * Angular `FileUploadURLComponent` — `POST /api/version_file_upload` multipart
 * with field **`version_image`**; response body is the hosted file URL (string) for CKEditor.
 */
export async function uploadVersionImage(file: File): Promise<string> {
  try {
    const fd = new FormData()
    fd.append('version_image', file, file.name)
    const response = await apiClient.post<unknown>('/api/version_file_upload', fd)
    const data = response.data
    if (typeof data === 'string') {
      const s = data.trim()
      if (s) return s
    }
    if (data && typeof data === 'object' && data !== null && 'url' in data) {
      const u = (data as { url: unknown }).url
      if (typeof u === 'string' && u.trim()) return u.trim()
    }
    throw new Error('Upload response did not include a URL')
  } catch (error) {
    throw new Error(extractErrorMessage(error) || 'Image upload failed')
  }
}
